// Resume-tailor workshop — Session 3 wave 4 (plan §457–462).
//
// Reachable two ways:
//   /jobs/:companyId/:jobId/tailor          — deep-link from /jobs sidebar
//   /applications/:applicationId/resume     — deep-link from /applications
//
// Layout (per the plan):
//   Top:    JD summary + score axes
//   Middle: split view — Markdown preview (left) + raw editor (right)
//   Right:  "Source units" panel listing every career_unit used, with a
//           per-unit "Swap out" button
//   Header: Regenerate, Render PDF, Download .md, Copy
//
// Empty state — no version yet — surfaces a single "Tailor this resume"
// button that POSTs /api/resume-versions/tailor and lands the user in the
// editor on success.

import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { ChevronRight, Copy, Download, FileText, RefreshCw, X } from "lucide-react";
import type { CareerUnit, ResumeVersion } from "@resume-cloudflare/shared";
import { useCorpus } from "../lib/corpus";
import { useApplications } from "../lib/applications";
import { useJobByIds, type JobRow } from "../lib/jobs";
import {
  resumeVersionPdfUrl,
  useDeleteResumeVersion,
  usePatchResumeVersion,
  useResumeVersions,
  useSwapUnit,
  useTailor,
} from "../lib/resume_versions";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { EmptyState } from "../components/ui/EmptyState";
import { Pill } from "../components/ui/Pill";
import { SkeletonText } from "../components/ui/Skeleton";
import { Textarea } from "../components/ui/Textarea";
import { Input } from "../components/ui/Input";
import { ScorePill } from "../components/jobs/ScorePill";
import { renderMarkdownPreview } from "../lib/markdown_preview";

export default function ResumeTailor() {
  const params = useParams<{
    companyId?: string;
    jobId?: string;
    applicationId?: string;
  }>();

  if (params.companyId && params.jobId) {
    return (
      <ResumeTailorByJob companyId={params.companyId} jobId={params.jobId} />
    );
  }
  if (params.applicationId) {
    return <ResumeTailorByApp applicationId={params.applicationId} />;
  }
  return (
    <section className="p-6">
      <Card>
        <EmptyState
          tone="danger"
          title="Tailor route invoked without a job or application id."
        />
      </Card>
    </section>
  );
}

// /applications/:applicationId/resume — resolve the application to its job
// (companyId, jobId) via /api/applications + /api/jobs filtered by URL.
// `applications` rows don't carry job_id directly, so we hop through the
// applications list and match by URL/company.
function ResumeTailorByApp({ applicationId }: { applicationId: string }) {
  const apps = useApplications();
  const app = apps.data?.applications.find((a) => a.id === applicationId);

  // Without a (companyId, jobId) we can't render the workshop. The
  // applications row carries `company_id` (nullable for ad-hoc URLs); the
  // user has to pick a tailored resume for a tracked job.
  if (apps.isLoading) {
    return (
      <section className="p-6 space-y-4">
        <SkeletonText lines={3} />
      </section>
    );
  }
  if (apps.error || !app) {
    return (
      <section className="p-6">
        <Card>
          <EmptyState
            tone="danger"
            title="Application not found"
            description="This application id isn't visible to your account."
          />
        </Card>
      </section>
    );
  }

  if (!app.company_id) {
    return (
      <section className="p-6 space-y-4">
        <Card className="p-5">
          <h2 className="text-base font-semibold text-neutral-50">
            This application isn't linked to a tracked job.
          </h2>
          <p className="mt-2 text-sm text-neutral-400">
            Tailoring needs the JD body. Pick this role from the{" "}
            <Link className="text-accent-300" to="/jobs">
              Jobs
            </Link>{" "}
            page and use "Tailor resume" there.
          </p>
        </Card>
      </section>
    );
  }

  // We have company_id but not job_id. Find the matching job by URL — the
  // applications.url matches jobs.url 1:1 in the snapshot. Jobs route's
  // single-job endpoint needs (companyId, jobId), so we list jobs filtered
  // by company and find the URL match. Cheap because companies have ≤ a
  // few hundred jobs each.
  return (
    <ResumeTailorByAppResolveJob
      applicationId={applicationId}
      companyId={app.company_id}
      url={app.url}
    />
  );
}

function ResumeTailorByAppResolveJob({
  applicationId,
  companyId,
  url,
}: {
  applicationId: string;
  companyId: string;
  url: string;
}) {
  // Fetch a single page of jobs for this company + match by URL. There's no
  // dedicated "find by URL" endpoint today; iterating company-scoped pages
  // is OK because companies have ≤ a few hundred active jobs.
  const [jobId, setJobId] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const params = new URLSearchParams({ company: companyId, limit: "500" });
      const res = await fetch(`/api/jobs?${params.toString()}`, {
        credentials: "same-origin",
      });
      if (!res.ok) return;
      const body = (await res.json()) as { jobs: JobRow[] };
      const match = body.jobs.find((j) => j.url === url);
      if (!cancelled) setJobId(match?.job_id ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, url]);

  if (jobId === null) {
    return (
      <section className="p-6">
        <Card className="p-5 space-y-2">
          <h2 className="text-base font-semibold text-neutral-50">
            Resolving job…
          </h2>
          <SkeletonText lines={2} />
        </Card>
      </section>
    );
  }

  return (
    <ResumeTailorByJob
      companyId={companyId}
      jobId={jobId}
      applicationId={applicationId}
    />
  );
}

function ResumeTailorByJob({
  companyId,
  jobId,
  applicationId,
}: {
  companyId: string;
  jobId: string;
  applicationId?: string;
}) {
  const job = useJobByIds(companyId, jobId);
  const versions = useResumeVersions({ applicationId });
  const corpus = useCorpus();

  const [params, setParams] = useSearchParams();
  const versionParam = params.get("version");

  // Pick which version to display: explicit ?version= takes precedence;
  // otherwise the most recently created one for this app.
  const versionsForJd = useMemo(() => {
    const all = versions.data?.versions ?? [];
    if (applicationId) return all.filter((v) => v.application_id === applicationId);
    // Without an application_id we can only match by label heuristic
    // ("{Company} — {Title}"). Imperfect — UI should prefer applicationId.
    if (job.data) {
      const label = `${job.data.job.company_name} — ${job.data.job.title}`;
      return all.filter((v) => v.label === label);
    }
    return all;
  }, [versions.data, job.data, applicationId]);

  const selected: ResumeVersion | undefined =
    versionsForJd.find((v) => v.id === versionParam) ?? versionsForJd[0];

  function setVersion(id: string | null) {
    const next = new URLSearchParams(params);
    if (id) next.set("version", id);
    else next.delete("version");
    setParams(next, { replace: true });
  }

  if (job.isLoading) {
    return (
      <section className="p-6 space-y-4">
        <SkeletonText lines={3} />
      </section>
    );
  }
  if (job.error || !job.data) {
    return (
      <section className="p-6">
        <Card>
          <EmptyState
            tone="danger"
            title="Job not found"
            description={`Couldn't load ${companyId}/${jobId}.`}
          />
        </Card>
      </section>
    );
  }

  const allUnits = corpus.data?.units ?? [];

  return (
    <section className="space-y-5 p-4 sm:p-6 lg:p-8">
      <JobHeader job={job.data.job} />

      {versionsForJd.length > 1 ? (
        <VersionSwitcher
          versions={versionsForJd}
          selectedId={selected?.id ?? null}
          onSelect={setVersion}
        />
      ) : null}

      {!selected ? (
        <FirstTailorPanel
          companyId={companyId}
          jobId={jobId}
          applicationId={applicationId}
          onCreated={(id) => setVersion(id)}
        />
      ) : (
        <Workshop
          version={selected}
          companyId={companyId}
          jobId={jobId}
          applicationId={applicationId}
          allUnits={allUnits}
          onSwapped={(newId) => setVersion(newId)}
          onRegenerated={(newId) => setVersion(newId)}
          onDeleted={() => setVersion(null)}
        />
      )}
    </section>
  );
}

function JobHeader({ job }: { job: JobRow }) {
  const eligible = job.location_eligible_for_user;
  const eligibleClass =
    eligible === true
      ? "text-success-fg"
      : eligible === false
        ? "text-warning-fg"
        : "text-neutral-300";
  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight text-neutral-50">
            {job.title}
          </h1>
          <p className="mt-0.5 text-sm text-neutral-400">
            {job.company_name}
            {job.location_summary ? (
              <>
                {" "}·{" "}
                <span className={eligibleClass}>{job.location_summary}</span>
              </>
            ) : null}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {job.score !== null ? (
            <ScorePill score={job.score} />
          ) : (
            <Pill tone="neutral">Unscored</Pill>
          )}
          <a
            className="text-xs text-accent-300 hover:text-accent-200"
            href={job.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open posting →
          </a>
        </div>
      </div>
      {job.score_axes ? (
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
          <Axis label="Resp" value={job.score_axes.responsibilities} />
          <Axis label="Senior" value={job.score_axes.seniority} />
          <Axis label="Domain" value={job.score_axes.domain} />
          <Axis label="Tech" value={job.score_axes.tech_stack} />
          <Axis label="Loc fit" value={job.score_axes.location_fit} />
        </div>
      ) : null}
      {job.score_rationale ? (
        <p className="mt-3 text-xs text-neutral-500">{job.score_rationale}</p>
      ) : null}
    </Card>
  );
}

function Axis({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-neutral-500">
        {label}
      </p>
      <div className="mt-1 flex items-center gap-2">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-neutral-800">
          <div
            className="h-full bg-accent-500"
            style={{ width: `${(value / 10) * 100}%` }}
          />
        </div>
        <span className="text-xs tabular-nums text-neutral-300">{value}</span>
      </div>
    </div>
  );
}

function VersionSwitcher({
  versions,
  selectedId,
  onSelect,
}: {
  versions: ResumeVersion[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {versions.map((v, i) => {
        const active = v.id === selectedId;
        return (
          <button
            key={v.id}
            type="button"
            onClick={() => onSelect(v.id)}
            className={
              "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs " +
              (active
                ? "border-accent-500 bg-accent-500/10 text-neutral-50"
                : "border-neutral-700 text-neutral-400 hover:text-neutral-200")
            }
          >
            <span>v{versions.length - i}</span>
            <span className="text-neutral-500 tabular-nums">
              {new Date(v.created_at).toLocaleDateString()}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function FirstTailorPanel({
  companyId,
  jobId,
  applicationId,
  onCreated,
}: {
  companyId: string;
  jobId: string;
  applicationId?: string;
  onCreated: (id: string) => void;
}) {
  const tailor = useTailor();
  const [focus, setFocus] = useState("");

  async function run() {
    const res = await tailor.mutateAsync({
      company_id: companyId,
      job_id: jobId,
      application_id: applicationId,
      focus: focus.trim() || undefined,
    });
    onCreated(res.version.id);
  }

  return (
    <Card className="p-6 space-y-4">
      <div>
        <h2 className="text-base font-semibold text-neutral-50">
          Tailor a resume for this role
        </h2>
        <p className="mt-1 text-sm text-neutral-400">
          The composer reads your career corpus, ranks each unit against this
          JD, and writes a Markdown resume. Compose typically takes 15–25
          seconds.
        </p>
      </div>
      <div>
        <label className="block text-[11px] uppercase tracking-wider text-neutral-500">
          Optional focus
        </label>
        <Textarea
          rows={3}
          className="mt-1"
          placeholder="e.g. emphasise pre-sales motion, light on hands-on coding"
          value={focus}
          onChange={(e) => setFocus(e.target.value)}
        />
      </div>
      <Button
        variant="primary"
        onClick={run}
        loading={tailor.isPending}
        leadingIcon={<RefreshCw size={14} />}
      >
        Tailor resume
      </Button>
    </Card>
  );
}

function Workshop({
  version,
  companyId,
  jobId,
  applicationId,
  allUnits,
  onSwapped,
  onRegenerated,
  onDeleted,
}: {
  version: ResumeVersion;
  companyId: string;
  jobId: string;
  applicationId?: string;
  allUnits: CareerUnit[];
  onSwapped: (id: string) => void;
  onRegenerated: (id: string) => void;
  onDeleted: () => void;
}) {
  const patch = usePatchResumeVersion();
  const swap = useSwapUnit();
  const tailor = useTailor();
  const del = useDeleteResumeVersion();

  // Local edit buffer — save-on-blur to /api/resume-versions/:id PATCH.
  const [draft, setDraft] = useState(version.markdown);
  const [labelDraft, setLabelDraft] = useState(version.label);
  const [focus, setFocus] = useState("");

  // Reset local buffers if the underlying version changes (swap → new id).
  useEffect(() => {
    setDraft(version.markdown);
    setLabelDraft(version.label);
  }, [version.id, version.markdown, version.label]);

  const dirty = draft !== version.markdown;
  const labelDirty =
    labelDraft.trim().length > 0 && labelDraft !== version.label;

  async function saveMarkdown() {
    if (!dirty) return;
    await patch.mutateAsync({ id: version.id, patch: { markdown: draft } });
  }
  async function saveLabel() {
    if (!labelDirty) return;
    await patch.mutateAsync({
      id: version.id,
      patch: { label: labelDraft.trim() },
    });
  }

  async function regenerate() {
    const res = await tailor.mutateAsync({
      company_id: companyId,
      job_id: jobId,
      application_id: applicationId,
      focus: focus.trim() || undefined,
      excluded_unit_ids: [],
    });
    onRegenerated(res.version.id);
  }

  async function dropUnit(unitId: string) {
    const res = await swap.mutateAsync({
      id: version.id,
      companyId,
      jobId,
      unitIdToDrop: unitId,
    });
    onSwapped(res.version.id);
  }

  async function copyMd() {
    await navigator.clipboard.writeText(draft);
  }

  function downloadMd() {
    const blob = new Blob([draft], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${slugify(version.label) || version.id}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function remove() {
    if (!confirm("Delete this tailored resume version?")) return;
    await del.mutateAsync(version.id);
    onDeleted();
  }

  const unitsById = useMemo(() => {
    const m = new Map<string, CareerUnit>();
    for (const u of allUnits) m.set(u.id, u);
    return m;
  }, [allUnits]);

  const previewHtml = useMemo(() => renderMarkdownPreview(draft), [draft]);

  return (
    <div className="space-y-5">
      <Card className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Input
            value={labelDraft}
            onChange={(e) => setLabelDraft(e.target.value)}
            onBlur={saveLabel}
            className="max-w-md"
            placeholder="Label"
          />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={copyMd}
              leadingIcon={<Copy size={14} />}
            >
              Copy
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={downloadMd}
              leadingIcon={<Download size={14} />}
            >
              .md
            </Button>
            <a
              href={resumeVersionPdfUrl(version.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-8 items-center gap-2 rounded-md border border-neutral-700 bg-neutral-900 px-3 text-xs font-medium text-neutral-200 hover:border-accent-500 hover:text-neutral-50"
            >
              <FileText size={14} /> Render PDF
            </a>
            <Button
              variant="primary"
              size="sm"
              onClick={regenerate}
              loading={tailor.isPending}
              leadingIcon={<RefreshCw size={14} />}
            >
              Regenerate
            </Button>
          </div>
        </div>
        <div className="mt-3">
          <Textarea
            rows={2}
            className="text-xs"
            placeholder="Optional feedback to steer regenerate (e.g. 'emphasize networking, drop research bullets')"
            value={focus}
            onChange={(e) => setFocus(e.target.value)}
          />
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_28rem]">
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <Card className="p-5">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-xs font-medium uppercase tracking-wider text-neutral-500">
                  Preview
                </h3>
                {patch.isPending ? (
                  <span className="text-[10px] text-neutral-500">saving…</span>
                ) : null}
              </div>
              <article
                className="prose-resume max-w-none text-sm text-neutral-200"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </Card>
            <Card className="p-5">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-xs font-medium uppercase tracking-wider text-neutral-500">
                  Markdown
                </h3>
                <span className="text-[10px] text-neutral-500">
                  {dirty ? "unsaved" : "saved"}
                </span>
              </div>
              <Textarea
                rows={28}
                className="font-mono text-[11.5px] leading-relaxed"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={saveMarkdown}
                spellCheck={false}
              />
            </Card>
          </div>
        </div>

        <SourceUnitsPanel
          unitIds={version.source_unit_ids}
          unitsById={unitsById}
          onDrop={dropUnit}
          dropping={swap.isPending}
        />
      </div>

      <div className="flex justify-end">
        <Button variant="danger" size="sm" onClick={remove} loading={del.isPending}>
          Delete this version
        </Button>
      </div>
    </div>
  );
}

function SourceUnitsPanel({
  unitIds,
  unitsById,
  onDrop,
  dropping,
}: {
  unitIds: string[];
  unitsById: Map<string, CareerUnit>;
  onDrop: (id: string) => void;
  dropping: boolean;
}) {
  const grouped = useMemo(() => {
    const out: Record<string, CareerUnit[]> = {};
    for (const id of unitIds) {
      const u = unitsById.get(id);
      if (!u) continue;
      const key = u.kind;
      out[key] ??= [];
      out[key]!.push(u);
    }
    return out;
  }, [unitIds, unitsById]);

  const order: Array<keyof typeof grouped> = [
    "summary",
    "role",
    "bullet",
    "project",
    "skill",
    "education",
    "cert",
  ];

  const total = unitIds.length;

  return (
    <Card className="self-start p-5">
      <header className="mb-3 flex items-baseline justify-between gap-2">
        <h3 className="text-xs font-medium uppercase tracking-wider text-neutral-500">
          Source units
        </h3>
        <span className="text-xs tabular-nums text-neutral-500">{total}</span>
      </header>
      {total === 0 ? (
        <p className="text-sm text-neutral-500">
          No units cited yet. Re-run with feedback if the model under-cites.
        </p>
      ) : null}
      {order.map((kind) => {
        const items = grouped[kind] ?? [];
        if (items.length === 0) return null;
        return (
          <section key={kind} className="mt-3">
            <p className="text-[10px] uppercase tracking-wider text-neutral-500">
              {kind} ({items.length})
            </p>
            <ul className="mt-1 space-y-1.5">
              {items.map((u) => (
                <li
                  key={u.id}
                  className="group flex items-start gap-2 rounded-md border border-neutral-800 p-2 hover:border-neutral-700"
                >
                  <ChevronRight
                    size={12}
                    className="mt-0.5 text-neutral-600 group-hover:text-neutral-400"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-neutral-200 line-clamp-2">
                      {u.title || u.content}
                    </p>
                    {u.title && u.content && u.content !== u.title ? (
                      <p className="mt-0.5 text-[11px] text-neutral-500 line-clamp-2">
                        {u.content}
                      </p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => onDrop(u.id)}
                    disabled={dropping}
                    title="Swap this unit out and re-tailor"
                    className="rounded p-1 text-neutral-500 opacity-0 hover:bg-neutral-800 hover:text-neutral-100 focus-visible:opacity-100 group-hover:opacity-100 disabled:opacity-50"
                  >
                    <X size={12} />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </Card>
  );
}

function slugify(s: string): string {
  return s
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}
